import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConversations, deleteConversation } from '../api/conversations';
import type { ConversationSummary } from '@/types/conversation';

// Query key factory
export const conversationKeys = {
  all: ['conversations'] as const,
  detail: (id: string) => ['conversations', id] as const,
};

/**
 * Hook for conversations list
 */
export function useConversations() {
  return useQuery<ConversationSummary[]>({
    queryKey: conversationKeys.all,
    queryFn: getConversations,
  });
}

/**
 * Hook for deleting conversation
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteConversation,

    // Optimistic update
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.all });

      const previousConversations =
        queryClient.getQueryData<ConversationSummary[]>(conversationKeys.all);

      queryClient.setQueryData<ConversationSummary[]>(
        conversationKeys.all,
        (old) => old?.filter((c) => c.id !== conversationId) || []
      );

      return { previousConversations };
    },

    onError: (_err, _conversationId, context) => {
      // Rollback on error
      if (context?.previousConversations) {
        queryClient.setQueryData(
          conversationKeys.all,
          context.previousConversations
        );
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}
